import { buildCrossTable } from '../lib/build-cross-table';
import type { SouscriptionEntry } from '../types';

/**
 * Tableau croisé compagnie × mois. Compagnie sticky à gauche, mois triés
 * ASC en colonnes, total à droite + footer avec totaux par mois.
 *
 * Scroll horizontal si beaucoup de mois (rare sur une période <= 12 mois).
 */
export interface TableauCroiseCompagnieMoisProps {
  entries: SouscriptionEntry[];
  dateField: 'dateSouscription' | 'dateEffet';
}

export function TableauCroiseCompagnieMois({
  entries,
  dateField,
}: TableauCroiseCompagnieMoisProps) {
  const { months, rows, monthTotals, grandTotal } = buildCrossTable(entries, dateField);

  if (rows.length === 0) {
    return (
      <div className="rounded-stripe border border-stripe-border bg-white p-8 text-center shadow-stripe-sm">
        <div className="text-sm font-semibold text-stripe-text">Détail par compagnie et mois</div>
        <div className="mt-2 text-2xs text-stripe-text-muted">
          Aucune donnée pour la période sélectionnée.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-stripe border border-stripe-border bg-white shadow-stripe-sm">
      <div className="border-b border-stripe-border-light px-4 py-3">
        <div className="text-sm font-semibold text-stripe-text">Détail par compagnie et mois</div>
        <div className="mt-0.5 text-2xs text-stripe-text-muted">
          {dateField === 'dateEffet' ? `Par date d'effet` : 'Par date de souscription'}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] tabular-nums">
          <thead>
            <tr className="border-b border-stripe-border-light">
              <th className="sticky left-0 z-10 bg-white px-3 py-2 text-left text-2xs font-medium uppercase tracking-wide text-stripe-text-muted">
                Compagnie
              </th>
              {months.map((m) => (
                <th
                  key={m.key}
                  className="px-3 py-2 text-right text-2xs font-medium uppercase tracking-wide text-stripe-text-muted"
                >
                  {m.label}
                </th>
              ))}
              <th className="px-3 py-2 text-right text-2xs font-medium uppercase tracking-wide text-stripe-text-muted">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.company} className="hover:bg-stripe-hover">
                <td className="sticky left-0 z-10 bg-white px-3 py-2 font-medium text-stripe-text">
                  {r.company}
                </td>
                {months.map((m) => {
                  const v = r.byMonth[m.key] ?? 0;
                  return (
                    <td key={m.key} className="px-3 py-2 text-right text-stripe-text-secondary">
                      {v > 0 ? (
                        v.toLocaleString('fr-FR')
                      ) : (
                        <span className="text-stripe-text-placeholder">—</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right font-medium text-stripe-text">
                  {r.total.toLocaleString('fr-FR')}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-stripe-border-light bg-stripe-bg/30">
              <td className="sticky left-0 z-10 bg-stripe-bg/30 px-3 py-2 text-2xs font-medium uppercase tracking-wide text-stripe-text-muted">
                Total
              </td>
              {months.map((m) => (
                <td key={m.key} className="px-3 py-2 text-right font-medium text-stripe-text">
                  {(monthTotals[m.key] ?? 0).toLocaleString('fr-FR')}
                </td>
              ))}
              <td className="px-3 py-2 text-right font-semibold text-stripe-accent">
                {grandTotal.toLocaleString('fr-FR')}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
